import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Box, Typography } from '@mui/material';

import { Text } from '@app/ui/forms';

import { RuleAppliesTo, RuleField } from '../../types';

export interface MatchingFieldAutocompleteProps {
  readonly fields: RuleField[];
  readonly value: RuleField | null;
  readonly disabled: boolean;
  readonly label: ReactNode;
  readonly helperText?: ReactNode;
  readonly error?: string;
  readonly onChange: (next: RuleField | null) => void;
  readonly onBlur?: () => void;
}

const APPLIES_TO_ORDER: RuleAppliesTo[] = [RuleAppliesTo.self, RuleAppliesTo.partner];

// `ConditionRow`'s field-picker shape. The matching catalogue repeats labels (24× "Please explain"), and
// MUI keys an option by its label unless `renderOption` sets `key`, so each option is keyed and captioned by
// its path. `groupBy` only merges ADJACENT options, and the catalogue interleaves self/partner entries.
export const MatchingFieldAutocomplete = ({
  fields,
  value,
  disabled,
  label,
  helperText,
  error,
  onChange,
  onBlur,
}: MatchingFieldAutocompleteProps) => {
  const { t } = useTranslation('common');

  const options = useMemo(
    () =>
      [...fields].sort(
        (left, right) => APPLIES_TO_ORDER.indexOf(left.appliesTo) - APPLIES_TO_ORDER.indexOf(right.appliesTo),
      ),
    [fields],
  );

  const fieldMeta = (field: RuleField) =>
    t('rules.editor.condition.fieldMeta', { path: field.path, type: t(`rules.fieldType.option.${field.type}`) });

  return (
    <Autocomplete
      disabled={disabled}
      options={options}
      value={value}
      groupBy={option => t(`rules.editor.appliesTo.${option.appliesTo}`) ?? option.appliesTo}
      getOptionLabel={option => option.label}
      isOptionEqualToValue={(option, selected) => option.path === selected.path}
      onChange={(_event, next) => onChange(next)}
      onBlur={onBlur}
      renderOption={(props, option) => (
        <li {...props} key={option.path}>
          <Box>
            <Typography variant="body1">{option.label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {fieldMeta(option)}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={params => (
        <Text {...params} label={label} error={!!error} helperText={error ?? (value ? fieldMeta(value) : helperText)} />
      )}
    />
  );
};
